import {createAsyncThunk} from '@reduxjs/toolkit';
import {
    EP_CONVERSIONS_PERFORMANCES_INFLUENCERS,
    EP_CONVERSIONS_PERFORMANCES_SOCIALMEDIA,
    EP_CONVERSIONS_PERFORMANCES_SUBCATEGORIES
} from '@api/endPoints.js';
import {getData} from '@api/api.js';
import {endOfDay, getUnixTime, startOfDay} from 'date-fns';
import _ from "lodash";
import {chartArrayMapper, toPercent} from "../../utility/Utils";
import {influencerTableData, socialMediaTableData, subCategTableData} from "./conversion.mapper";
import { zonedTimeToUtc } from 'date-fns-tz';

const buildBaseParams = ({ startDate, endDate, brand, skip = 0, limit = 10, order_by = "sales_nbr", sort = "DESC" }) => ({
    brand_id: brand,
    start: getUnixTime(zonedTimeToUtc(startOfDay(startDate), "GMT")),
    end: getUnixTime(zonedTimeToUtc(endOfDay(endDate), "GMT")),
    offset: skip,
    count: limit,
    order_by,
    sort,
    timezone: new Date().getTimezoneOffset()
});

export const getConversionRate = createAsyncThunk(
    'conversion/getConversionRate',
    async ({ startDate, endDate, brand = 0 }, thunkAPI) => {
        try {
            const params = buildBaseParams({ startDate, endDate, brand, skip: 0, limit: 1, order_by: "sales_nbr", sort: "DESC" });
            const { Conversion_rate_total: conversionsRate } = await getData(EP_CONVERSIONS_PERFORMANCES_INFLUENCERS, { params });
            return {
                conversionsRate: toPercent(conversionsRate ? conversionsRate * 100 : 0),
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
)

export const getConversionsByInfluencer = createAsyncThunk(
    'conversion/getConversionsByInfluencer',
    async (
        {startDate, endDate, brand = 0, skip = 0, limit = 10, order_by = "sales_nbr", sort = "DESC"},
        thunkAPI
    ) => {
        try {
            const params = buildBaseParams({ startDate, endDate, brand, skip, limit, order_by, sort });

            const { data: influencerRows = [], total_count: totalCount = 0 } = await getData(EP_CONVERSIONS_PERFORMANCES_INFLUENCERS, {params});
            const conversionsPerInfsOrderBy = influencerRows ? _.orderBy(influencerRows, "sales_nbr", "desc") : [];

            return {
                influencer: {
                    chartData: chartArrayMapper(conversionsPerInfsOrderBy.slice(0, 6), 'influencer', 'sales_nbr'),
                    tableData: influencerTableData(influencerRows),
                    totalRows: Number(totalCount) || influencerRows.length || 0,
                },
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
)

export const getConversionsBySubcategory = createAsyncThunk(
    'conversion/getConversionsBySubcategory',
    async (
        {startDate, endDate, brand = 0, language = null, skip = 0, limit = 10, order_by = "sales_nbr", sort = "DESC"},
        thunkAPI
    ) => {
        try {
            const params = buildBaseParams({ startDate, endDate, brand, skip, limit, order_by, sort });
            const { data: conversionsPerSubCateg = [], total_count: totalCount = 0 } = await getData(EP_CONVERSIONS_PERFORMANCES_SUBCATEGORIES, { params });
            const conversionsPerSubCategOrderBy = conversionsPerSubCateg ? _.orderBy(conversionsPerSubCateg, "sales_nbr", "desc") : [];
            return {
                subCateg: {
                    chartData: chartArrayMapper(conversionsPerSubCategOrderBy.slice(0, 6), `${'text-' + language}`, 'sales_nbr'),
                    tableData: subCategTableData(conversionsPerSubCateg, language),
                    totalRows: Number(totalCount) || conversionsPerSubCateg.length || 0,
                },
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
)

export const getConversionsBySocialMedia = createAsyncThunk(
    'conversion/getConversionsBySocialMedia',
    async (
        {startDate, endDate, brand = 0, skip = 0, limit = 10, order_by = "sales_nbr", sort = "DESC"},
        thunkAPI
    ) => {
        try {
            const params = buildBaseParams({ startDate, endDate, brand, skip, limit, order_by, sort });
            const { data: conversionsPerSocialMedias = [], total_count: totalCount = 0 } = await getData(EP_CONVERSIONS_PERFORMANCES_SOCIALMEDIA, { params });
            const conversionsPerSocialMediasOrderBy = conversionsPerSocialMedias ? _.orderBy(conversionsPerSocialMedias, "sales_nbr", "desc") : [];
            return {
                socialMedia: {
                    chartData: chartArrayMapper(conversionsPerSocialMediasOrderBy.filter((s) => s.socialMedia).slice(0, 6), 'socialMedia', 'sales_nbr'),
                    tableData: socialMediaTableData(conversionsPerSocialMedias),
                    totalRows: Number(totalCount) || conversionsPerSocialMedias.length || 0,
                }
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
)
